// client/src/pages/Checkout.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement
} from '@stripe/react-stripe-js';

// Load Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK);

export default function CheckoutPage() {
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);

  // user data
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userAddress, setUserAddress] = useState('');
  const [userPhone, setUserPhone] = useState('');

  const [needsProfileUpdate, setNeedsProfileUpdate] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [profileError, setProfileError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('Please log in first.');
      window.location.href = '/login';
      return;
    }

    // fetch user profile
    axios.get(`${process.env.REACT_APP_API_URL}/api/user/profile`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => {
      const user = res.data;
      setUserName(user.name || '');
      setUserEmail(user.email || '');
      setUserAddress(user.address || '');
      setUserPhone(user.phone || '');

      // if missing address/phone => ask user to update
      if (!user.address || !user.phone) {
        setNeedsProfileUpdate(true);
        setLoading(false);
      } else {
        // create Stripe PaymentIntent
        createPaymentIntent();
      }
    })
    .catch(err => {
      console.error('Error fetching profile:', err);
      alert('Error. Please re-login.');
      window.location.href = '/login';
    });
  }, []);

  const createPaymentIntent = () => {
    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    const items = cart.map(item => ({
      price: item.price,
      quantity: item.quantity
    }));

    axios.post(`${process.env.REACT_APP_API_URL}/api/checkout/create-payment-intent`, { items })
      .then(res => {
        setClientSecret(res.data.clientSecret);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error creating PaymentIntent:', err);
        setLoading(false);
      });
  };

  const handleUpdateProfile = async() => {
    setProfileMsg('');
    setProfileError('');

    if (!userAddress || !userPhone) {
      setProfileError('Please fill in both address and phone.');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${process.env.REACT_APP_API_URL}/api/user/profile`, {
        name: userName,
        email: userEmail,
        address: userAddress,
        phone: userPhone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setProfileMsg('Profile updated. Proceeding to payment...');
      setNeedsProfileUpdate(false);
      createPaymentIntent();
    } catch (err) {
      console.error(err);
      setProfileError('Failed to update profile. Check console.');
    }
  };

  if (loading) {
    return <div className="p-4 mt-16">Loading checkout...</div>;
  }

  // If user must update address/phone
  if (needsProfileUpdate && !clientSecret) {
    return (
      <div className="max-w-md mx-auto p-4 mt-16">
        <h2 className="text-2xl font-bold mb-4">Update Info</h2>
        <p>Please enter your complete address and phone to continue:</p>

        <label className="block mt-4 font-medium">Name</label>
        <input
          type="text"
          className="border p-2 w-full"
          value={userName}
          onChange={e => setUserName(e.target.value)}
        />

        <label className="block mt-4 font-medium">Email</label>
        <input
          type="email"
          className="border p-2 w-full"
          value={userEmail}
          onChange={e => setUserEmail(e.target.value)}
        />

        <label className="block mt-4 font-medium">Address</label>
        <input
          type="text"
          className="border p-2 w-full"
          value={userAddress}
          onChange={e => setUserAddress(e.target.value)}
        />

        <label className="block mt-4 font-medium">Phone</label>
        <input
          type="text"
          className="border p-2 w-full"
          value={userPhone}
          onChange={e => setUserPhone(e.target.value)}
        />

        {profileError && <p className="text-red-500 mt-2">{profileError}</p>}
        {profileMsg && <p className="text-green-500 mt-2">{profileMsg}</p>}

        <button
          onClick={handleUpdateProfile}
          className="bg-primaryGreen text-white px-4 py-2 mt-4 rounded"
        >
          Save & Continue
        </button>
      </div>
    );
  }

  // If PaymentIntent was created => show Stripe form
  if (!clientSecret) {
    return <div className="p-4 mt-16">Error: No clientSecret. Try again.</div>;
  }

  const appearance = { theme: 'stripe' };
  const options = { clientSecret, appearance };

  return (
    <div className="max-w-md mx-auto p-4 mt-16">
      <h1 className="text-2xl font-bold mb-4">Complete Purchase</h1>
      <p className="text-sm mb-2">
        Name: {userName}<br/>
        Email: {userEmail}<br/>
        Address: {userAddress}<br/>
        Phone: {userPhone}
      </p>

      <Elements stripe={stripePromise} options={options}>
        <PaymentForm />
      </Elements>
    </div>
  );
}

// PaymentForm -> only card payments
function PaymentForm() {
  const stripe = useStripe();
  const elements = useElements();

  const [receiptEmail, setReceiptEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async(e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setMessage('');

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${process.env.REACT_APP_CLIENT_URL}/payment-success`,
        receipt_email: receiptEmail
      }
    });

    if (error) {
      setMessage(error.message || 'An unexpected error occurred.');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block mb-1 font-medium">Receipt Email</label>
        <input
          type="email"
          className="border p-2 w-full"
          placeholder="Email for Stripe receipt"
          value={receiptEmail}
          onChange={e => setReceiptEmail(e.target.value)}
          required
        />
      </div>

      <PaymentElement id="payment-element" />

      <button
        disabled={isProcessing || !stripe || !elements}
        className="bg-primaryGreen text-white px-4 py-2 rounded w-full"
      >
        {isProcessing ? 'Processing...' : 'Pay now'}
      </button>

      {message && <div className="text-red-500">{message}</div>}
    </form>
  );
}
